import { useEffect, useRef, useState } from "react";
import {
  ARCADA_DECIDUA_INFERIOR,
  ARCADA_DECIDUA_SUPERIOR,
  FDI_LOWER_ARCH,
  FDI_UPPER_ARCH,
  denticaoDoDente,
  denticaoInicial,
  ehDeciduo,
  type Denticao,
} from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { Arcada, type CondicaoDaArcada } from "@/components/odontograma/Arcada";
import { SeletorDeDenticao } from "@/components/odontograma/SeletorDeDenticao";

const botaoDeArcada = (ativo: boolean) =>
  cn(
    "w-full rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
    ativo
      ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
      : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 hover:bg-brand-50 dark:border-border dark:bg-card dark:text-muted-foreground",
  );

/**
 * A arcada do plano: dentes pintados pela cor de cada procedimento e, quando dá pra editar, a
 * seleção dos dentes do próximo item — já na cor do procedimento escolhido. Permanente ou decídua
 * (criança abre na decídua); a seleção das duas convive no mesmo item.
 */
export function OdontogramaDoPlano({
  condicoes,
  temItens,
  destacado,
  onLimparDestaque,
  selecionados,
  chave,
  corDaSelecao,
  editavel,
  nascimento,
  dentesDestacados = [],
  onSelecionar,
  onArcada,
}: {
  condicoes: CondicaoDaArcada[];
  temItens: boolean;
  destacado: string | null;
  onLimparDestaque: () => void;
  selecionados: string[];
  chave: number;
  corDaSelecao: string | null;
  editavel: boolean;
  nascimento?: Date | string | null;
  /** Dentes do item destacado na lista — a arcada vai pra dentição deles, senão o destaque some. */
  dentesDestacados?: string[];
  onSelecionar: (fdi: string[]) => void;
  onArcada: (arcada: string[], marcada: boolean) => void;
}) {
  const dentesDoPlano = condicoes.flatMap((c) => c.dentes);
  const inicial = () => denticaoInicial({ dentes: selecionados.length ? selecionados : dentesDoPlano, nascimento });
  const [denticao, setDenticao] = useState<Denticao>(inicial);
  const tocou = useRef(false);
  // Editar um item (a chave muda) abre na dentição dele; a data de nascimento pode chegar depois.
  useEffect(() => {
    if (selecionados.length) setDenticao(denticaoInicial({ dentes: selecionados }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);
  useEffect(() => {
    if (!tocou.current) setDenticao(inicial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nascimento]);

  useEffect(() => {
    if (dentesDestacados.length && !dentesDestacados.some((d) => denticaoDoDente(d) === denticao)) {
      setDenticao(denticaoDoDente(dentesDestacados[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dentesDestacados.join(",")]);

  const sup = denticao === "decidua" ? ARCADA_DECIDUA_SUPERIOR : FDI_UPPER_ARCH;
  const inf = denticao === "decidua" ? ARCADA_DECIDUA_INFERIOR : FDI_LOWER_ARCH;
  const superior = sup.every((f) => selecionados.includes(f));
  const inferior = inf.every((f) => selecionados.includes(f));
  const deciduos = selecionados.filter(ehDeciduo).length;

  return (
    <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
      <SeletorDeDenticao
        valor={denticao}
        onMudar={(d) => {
          tocou.current = true;
          setDenticao(d);
        }}
        contagem={{ permanente: selecionados.length - deciduos, decidua: deciduos }}
      />
      {editavel && (
        <button type="button" className={botaoDeArcada(superior)} onClick={() => onArcada(sup, superior)}>
          {denticao === "decidua" ? "Arcada decídua superior" : "Arcada superior"}
        </button>
      )}
      <div className="mx-auto w-full max-w-[170px] lg:max-w-[220px]">
        <Arcada
          denticao={denticao}
          selecionados={selecionados}
          chave={chave}
          condicoes={condicoes}
          corDaSelecao={corDaSelecao}
          somenteLeitura={!editavel}
          onSelecionar={onSelecionar}
        />
      </div>
      {editavel && (
        <button type="button" className={botaoDeArcada(inferior)} onClick={() => onArcada(inf, inferior)}>
          {denticao === "decidua" ? "Arcada decídua inferior" : "Arcada inferior"}
        </button>
      )}
      {temItens && (
        <p className="border-t border-zinc-100 pt-3 text-[11px] text-zinc-500 dark:border-border">
          {destacado ? (
            <>
              Destacando <b className="text-zinc-700 dark:text-foreground">{destacado}</b> ·{" "}
              <button type="button" className="font-semibold text-brand-700 hover:underline" onClick={onLimparDestaque}>
                mostrar todos
              </button>
            </>
          ) : (
            "Cor clara: planejado · cor cheia: feito. Passe o mouse (ou toque) num procedimento para ver os dentes dele."
          )}
        </p>
      )}
    </div>
  );
}
